<script>
import { mapGetters } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { fieldValidationsMixin } from '@/mixins/fieldValidationsMixin'
import Avv from '@/components/common/Avv'
import EmptyProfile from '@/components/profile/EmptyProfile.vue'
import SmallTabHeader from "@/components/common/tabs/SmallTabHeader.vue";

export default {
  name: 'SftpForm',
  components: { SmallTabHeader, Avv, EmptyProfile },
  mixins: [validationMixin, fieldValidationsMixin],
  validations: {
    sftpHost: { required },
    sftpUser: { required },
    sftpPassword: { required }
  },
  data: () => ({
    sftpHost: '',
    sftpUser: '',
    sftpPassword: '',
    sftpAutoComplete: false,
    sftpAutoFileDelete: false,
    showSftpPwd: false,
    loading: false,
    deleting: false,
    saveError: null
  }),
  computed: {
    ...mapGetters(['hasCustomerNumber', 'hasAVV', 'locale']),
    connectionFailedError() {
      if (!this.$v.sftpHost.$dirty) return []
      return this.saveError === this.$enums.SftpConnection.ConnectHostFailed
        ? [this.$t('component.profile.uploadServices.sftpForm.sftp.error.connect.failed.field.message')]
        : []
    },
    userLoginError() {
      if (!this.$v.sftpHost.$dirty) return []
      return this.saveError === this.$enums.SftpConnection.UserLoginFailed
        ? [this.$t('component.profile.uploadServices.sftpForm.sftp.error.user.login.failed.field.message')]
        : []
    },
    userLoginErrorNoMsg() {
      return this.saveError === this.$enums.SftpConnection.UserLoginFailed ? [''] : []
    },
    isEmptySFTP() {
      return !this.sftpUser || !this.sftpHost || !this.sftpPassword
    },
    showSupport() {
      return this.saveError === this.$enums.SftpConnection.Unknown
    }
  },
  created() {
    this.sftpGet()
  },
  methods: {
    sftpGet() {
      this.loading = true
      this.$sftpService.get()
        .then(response => {
          this.sftpHost = response.sftpHost
          this.sftpUser = response.sftpUser
          this.sftpPassword = response.sftpPassword
          this.sftpAutoComplete = response.sftpAutoComplete
          this.sftpAutoFileDelete = response.sftpAutoFileDelete
        })
        .catch(() => {
          this.$notify(this.$t('component.profile.uploadServices.sftpForm.sftp.error.message.load'), { type: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    sftpSave() {
      this.loading = true
      this.saveError = this.$enums.SftpConnection.Success // reset error
      this.$v.$touch()
      if (this.$v.$invalid)
        return (this.loading = false)
      this.$sftpService.set({
        sftpHost: this.sftpHost,
        sftpUser: this.sftpUser,
        sftpPassword: this.sftpPassword,
        sftpAutoComplete: this.sftpAutoComplete,
        sftpAutoFileDelete: this.sftpAutoFileDelete
      })
        .then(response => {
          const result = response.sftpConnectionResult
          this.saveError = result
          if (result === this.$enums.SftpConnection.Success) {
            this.$notify(this.$t('component.profile.uploadServices.sftpForm.sftp.success.message'), { type: 'success' })
          } else {
            this.$v.$touch()
            this.$notify(this.getErrorMessage(result), { type: 'error', })
          }
        })
        .catch(() => {
          this.saveError = this.$enums.SftpConnection.Unknown
          this.$v.$touch()
          this.$notify(this.getErrorMessage(this.saveError), { type: 'error'})
        })
        .finally(() => this.loading = false)
    },
    sftpDelete() {
      this.deleting = true
      this.$v.$reset()
      return this.$sftpService.del()
        .then(() => {
          this.$notify(this.$t('component.profile.uploadServices.sftpForm.sftp.success.message.delete'), { type: 'success' })
        })
        .catch(e => {
          if (e.response?.data !== 'SFTPConfiguration not found') {
            this.$notify(this.$t('component.profile.uploadServices.sftpForm.sftp.error.message.delete'), { type: 'error'})
          }
        })
        .finally(() => {
          this.sftpHost = ''
          this.sftpUser = ''
          this.sftpPassword = ''
          this.deleting = false
          this.sftpGet()
        })
    },
    getErrorMessage(code) {
      const messages = {
        [this.$enums.SftpConnection.ConnectHostFailed]: this.$t('component.profile.uploadServices.sftpForm.sftp.error.connect.failed.message'),
        [this.$enums.SftpConnection.UserLoginFailed]: this.$t('component.profile.uploadServices.sftpForm.sftp.error.user.login.failed.message'),
        [this.$enums.SftpConnection.Unknown]: this.$t('component.profile.uploadServices.sftpForm.sftp.error.unknown.message')
      }
      return messages[code] || this.$t('component.profile.uploadServices.sftpForm.sftp.error.unknown.message')
    }
  }
}
</script>
<template>
  <v-container fluid>
    <SmallTabHeader
      :title="$t('component.profile.uploadServices.sftpForm.sftp.header')"
      subtitle=""
      :is-action-visible="false"/>

    <v-card outlined>
      <v-card-text v-if="!hasCustomerNumber && $store.getters.selectedOrganization.isAdmin">
        <EmptyProfile>{{ $t('component.profile.uploadServices.sftpForm.empty.profile.alert') }}</EmptyProfile>
      </v-card-text>
      <v-card-text v-show="!hasAVV">
        <v-row class="justify-center text-center">
          <v-col cols="auto">
            <Avv
            ><template slot="title">{{$t('component.profile.uploadServices.sftpForm.accept.avv.title')}}</template></Avv>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-text
        v-show="(hasAVV && hasCustomerNumber) || !$store.getters.selectedOrganization.isAdmin"
        class="pt-0">
        <v-row align="center" justify="center">
          <v-col cols="12">
            <form>
              <v-row>
                <v-col>
                  <v-text-field
                    :id="$t('component.profile.uploadServices.sftpForm.sftp.server')"
                    v-model="sftpHost"
                    :name="$t('component.profile.uploadServices.sftpForm.sftp.server')"
                    :label="$t('component.profile.uploadServices.sftpForm.sftp.server')"
                    :error-messages="[
                      ...fieldNeccessary($v.sftpHost, $v),
                      ...connectionFailedError
                    ]"
                    outlined
                    dense
                    required
                    @input="$v.sftpHost.$touch()"
                    @blur="$v.sftpHost.$touch()"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    :id="$t( 'component.profile.uploadServices.sftpForm.sftp.user')"
                    v-model="sftpUser"
                    :name="$t('component.profile.uploadServices.sftpForm.sftp.user')"
                    :label="$t('component.profile.uploadServices.sftpForm.sftp.user')"
                    :error-messages="[
                      ...fieldNeccessary($v.sftpUser, $v),
                      ...userLoginErrorNoMsg
                    ]"
                    aria-autocomplete="inline"
                    autocomplete="username"
                    outlined
                    dense
                    required
                    @input="$v.sftpUser.$touch()"
                    @blur="$v.sftpUser.$touch()"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    :id="$t('component.profile.uploadServices.sftpForm.sftp.password')"
                    v-model="sftpPassword"
                    :name="$t('component.profile.uploadServices.sftpForm.sftp.password')"
                    :label="$t('component.profile.uploadServices.sftpForm.sftp.password')"
                    :error-messages="[
                      ...fieldNeccessary($v.sftpPassword, $v),
                      ...userLoginError
                    ]"
                    :type="showSftpPwd ? 'text' : 'password'"
                    :append-icon="showSftpPwd ? 'mdi-eye' : 'mdi-eye-off'"
                    outlined
                    dense
                    required
                    autocomplete="new-password"
                    @input="$v.sftpPassword.$touch()"
                    @blur="$v.sftpPassword.$touch()"
                    @click:append="showSftpPwd = !showSftpPwd"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-checkbox
                    v-model="sftpAutoComplete"
                    :label="$t('component.profile.uploadServices.sftpForm.sftp.auto.complete.checkbox')"
                    class="mt-0"
                    hide-details
                  ></v-checkbox>
                </v-col>
                <v-col cols="12">
                  <v-checkbox
                    v-model="sftpAutoFileDelete"
                    :label="$t('component.profile.uploadServices.sftpForm.sftp.auto.file.delete.checkbox')"
                    class="mt-0"
                    hide-details
                  ></v-checkbox>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <span class="group">
                    <v-icon>mdi-information-outline</v-icon>
                    {{ $t('component.profile.uploadServices.sftpForm.sftp.info.text') }}
                  </span>
                </v-col>
              </v-row>
            </form>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-text v-if="showSupport">
      </v-card-text>
      <v-card-actions
        v-show="(hasAVV && hasCustomerNumber) || !$store.getters.selectedOrganization.isAdmin">
        <v-row class="justify-center text-center">
          <v-col cols="auto" class="pa-0 ma-0">
            <v-row>
              <v-col
                class="pt-0"
                :class="$vuetify.breakpoint.smAndDown ? 'pb-4' : ''">
                <v-btn
                  id="saveSftp"
                  :loading="loading"
                  class="text-center px-8"
                  color="primary"
                  depressed
                  @click.stop="sftpSave"
                >{{ $t('component.profile.uploadServices.sftpForm.sftp.label') }}
                </v-btn>
              </v-col>
              <v-col class="pt-0">
                <v-btn
                  v-if="!isEmptySFTP"
                  id="deleteSftp"
                  :loading="deleting"
                  class="text-center px-8"
                  color="primary"
                  depressed
                  @click.stop="sftpDelete"
                >{{
                    $vuetify.breakpoint.smAndDown
                      ? $t('component.profile.uploadServices.sftpForm.sftp.label.delete')
                      : $t('component.profile.uploadServices.sftpForm.sftp.label.delete.mobile')
                  }}</v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-container>
</template>
